import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { presupuestoProformaService } from '../services/presupuestoProformaService';
import { leyendasValidas } from '../utils/leyendas';
import { EditorLeyendas } from './EditorLeyendas';

interface LeyendasPresupuestoCardProps {
  notas: string[];
  onGuardar: (notas: string[]) => Promise<unknown>;
  readOnly?: boolean;
  isGuardando?: boolean;
}

/**
 * Leyendas propias del presupuesto, que se imprimen al pie de los dos PDF. Al
 * crearse, el presupuesto recibe una copia de las predeterminadas de
 * Configuración del Sistema.
 */
export const LeyendasPresupuestoCard: React.FC<LeyendasPresupuestoCardProps> = ({
  notas,
  onGuardar,
  readOnly = false,
  isGuardando = false,
}) => {
  const [abierto, setAbierto] = useState(false);
  const [leyendas, setLeyendas] = useState<string[]>(notas);
  const [cargandoPredeterminadas, setCargandoPredeterminadas] = useState(false);

  // Se compara por contenido: el detalle se vuelve a pedir al editar ítems y eso
  // no debe pisar lo que se está escribiendo acá.
  const notasGuardadas = JSON.stringify(notas);
  useEffect(() => {
    setLeyendas(JSON.parse(notasGuardadas));
  }, [notasGuardadas]);

  const hayCambios = JSON.stringify(leyendas) !== notasGuardadas;

  const restablecer = async () => {
    setCargandoPredeterminadas(true);
    try {
      setLeyendas(await presupuestoProformaService.notasPredeterminadas());
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ?? 'No se pudieron obtener las leyendas predeterminadas.'
      );
    } finally {
      setCargandoPredeterminadas(false);
    }
  };

  const ocupado = isGuardando || cargandoPredeterminadas;

  return (
    <Card>
      <CardHeader className="py-3">
        <button
          type="button"
          onClick={() => setAbierto((actual) => !actual)}
          className="flex w-full items-center gap-2 text-left"
          aria-expanded={abierto}
        >
          {abierto ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <CardTitle className="text-base">Leyendas del PDF</CardTitle>
          <span className="ml-auto text-xs text-muted-foreground">
            {notas.length === 1 ? '1 leyenda' : `${notas.length} leyendas`}
          </span>
        </button>
      </CardHeader>

      {abierto && (
        <CardContent className="space-y-4">
          {readOnly ? (
            notas.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin leyendas: el PDF no imprime ninguna.</p>
            ) : (
              <ol className="list-decimal space-y-2 pl-6 text-sm">
                {notas.map((nota, indice) => (
                  <li key={indice} className="whitespace-pre-line">
                    {nota}
                  </li>
                ))}
              </ol>
            )
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Se imprimen numeradas al pie de los PDF del adquiriente y del transferente.
              </p>
              <EditorLeyendas leyendas={leyendas} onChange={setLeyendas} deshabilitado={ocupado} />
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="outline" onClick={() => void restablecer()} disabled={ocupado}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Restablecer predeterminadas
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setLeyendas(JSON.parse(notasGuardadas))}
                  disabled={ocupado || !hayCambios}
                >
                  Descartar
                </Button>
                <Button
                  onClick={() => void onGuardar(leyendas)}
                  disabled={ocupado || !hayCambios || !leyendasValidas(leyendas)}
                >
                  Guardar leyendas
                </Button>
              </div>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
};
